// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Data for the SOP governance screen, from the two contracts that actually
 * serve it:
 *
 *   * coverage ← Digital Twin `/twin/datacenter/v1/details/{site_id}`, the
 *                selected site's structural graph, reading each equipment
 *                entity's `hasSOP` attribute
 *   * register ← SOP Factory `GET /artifacts?artifact_type=sop`
 *
 * **Scoped to the selected site**, not the fleet. The site is shell state
 * (`useActiveSite`), set by clicking a site on fleet overview, so this is one
 * request rather than one per assigned site. A manager viewing the whole fleet
 * has selected nothing, and the screen says so rather than silently rolling
 * every site together — that would answer a question nobody asked and hide
 * which site is non-compliant.
 *
 * Nothing is seeded. Where a contract is silent the hook says so — `coverage`
 * comes back `unpublished` rather than 0%, and an empty register is an empty
 * register.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { twinQueryOptions } from './useDigitalTwin';
import { useAuth } from './useAuth';
import { useActiveSite } from './useActiveSite';
import { listSops } from '../services/sopFactoryService';
import { buildCoverage, toRegisterRows } from '../services/sopGovernanceSelectors';
import type { SiteGraph } from '../services/sopGovernanceSelectors';
import type { CoverageResult, SopRegisterRow } from '../types/sopGovernance';

/** Page size for the register. The catalogue is not site-scoped upstream. */
const REGISTER_PAGE_SIZE = 200;

export interface UseSopGovernanceResult {
  /** The site in scope, or null when none is selected (whole-fleet scope). */
  site: { siteId: string; label: string } | null;
  coverage: CoverageResult | null;
  coverageLoading: boolean;
  coverageError: boolean;
  register: SopRegisterRow[];
  registerLoading: boolean;
  registerError: boolean;
  /** The register is one page; more SOPs exist upstream than are shown. */
  registerTruncated: boolean;
}

export function useSopGovernance(): UseSopGovernanceResult {
  const { sites } = useAuth();
  const { siteId, isFleet } = useActiveSite();

  const active = useMemo(
    () => (isFleet ? undefined : sites.find(s => s.site_id === siteId)),
    [isFleet, sites, siteId]
  );
  // Structural graph only ('' include): binding lives on entity attributes, so
  // pulling telemetry would multiply the payload for data this screen ignores.
  // The key is shared with useDigitalTwin/useTwinPrefetch, so a site already
  // warmed elsewhere costs nothing here. The twin is asked by OMS site id;
  // a missing site_key does not mean the site has no graph.
  const asked = Boolean(active?.site_id);
  const twinQuery = useQuery({
    ...twinQueryOptions(active?.site_id ?? '', '' as const),
    enabled: asked,
  });

  const coverageLoading = asked && twinQuery.isPending;
  const coverageError = asked && twinQuery.isError;

  const registerQuery = useQuery({
    queryKey: ['sop-factory', 'artifacts', 'sop', REGISTER_PAGE_SIZE] as const,
    queryFn: () => listSops({ artifactType: 'sop', pageSize: REGISTER_PAGE_SIZE }),
  });

  const graphData = twinQuery.data;
  const coverage = useMemo(() => {
    if (!active || !graphData) return null;
    const graph: SiteGraph = {
      siteId: active.site_id,
      siteKey: active.site_key ?? '',
      label: active.label,
      entities: graphData.entities,
    };
    return buildCoverage([graph]);
  }, [active, graphData]);

  const register = useMemo(
    () => toRegisterRows(registerQuery.data?.items ?? []),
    [registerQuery.data]
  );

  return {
    site: active ? { siteId: active.site_id, label: active.label } : null,
    coverage,
    coverageLoading,
    coverageError,
    register,
    registerLoading: registerQuery.isPending,
    registerError: registerQuery.isError,
    registerTruncated: registerQuery.data
      ? registerQuery.data.total != null
        ? registerQuery.data.total > registerQuery.data.items.length
        : registerQuery.data.items.length >= REGISTER_PAGE_SIZE
      : false,
  };
}
